import React from "react";
import { Easing, interpolate, spring, useCurrentFrame } from "remotion";
import { theme } from "../../brand";
import { FLOAT_SHADOW, LOOK } from "../../b2c/look";
import { FrakLogo } from "../../components/FrakLogo";
import { COPY as MERCHANT_COPY, euros } from "../../merchant/copy";
import { type Merchant, MERCHANTS } from "../../merchant/merchants";
import { LogoTile } from "../../merchant/ui/Logo";
import { REEL, ReelFrame } from "../ui/ReelFrame";
import { SLOTS } from "./copy";

/**
 * Reel "La machine à sous" (9:16, silent, ~13.7s): a slot machine loaded
 * with the live brands. Three pulls of the lever; each time the three
 * reels spin and stop on the same brand, and the payout shows its live
 * reward ("jusqu'à X €", with the footnote). The three highest rewards,
 * in rising order. End card: "Des marques qui paient leurs ambassadeurs",
 * with the live brand count.
 *
 * Everything comes from the snapshot: re-render after `npm run
 * sync:brands`. Frame 0 is the cover (hook + machine at rest, three
 * different brands). Silent: the sound is added in Instagram.
 */

/** Timeline, in frames. Holds are reading time: never shorten one. */
const T = {
  /** Lever pulls; the reels start `spin` frames after each. */
  pulls: [14, 104, 194],
  spin: 5,
  /** Reel r spins for `run + r * stagger` frames (left to right stops). */
  run: 26,
  stagger: 6,
  /** The payout shows this long after a pull (all three reels stopped). */
  payout: 46,
  exit: 300,
  end: 311,
  duration: 410,
} as const;

export const SLOTS_DURATION = T.duration;

const M = MERCHANTS.length;

/** Highest rewards, shown in rising order (index into MERCHANTS). */
const WINNERS = MERCHANTS.map((m, i) => ({ m, i }))
  .sort((a, b) => b.m.reward - a.m.reward)
  .slice(0, T.pulls.length)
  .reverse()
  .map(({ i }) => i);

const GREEN = theme.brand.colors.success[800];
const FR = MERCHANT_COPY.fr;
const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;
const ramp = (frame: number, at: number, len: number, ease = Easing.out(Easing.cubic)) =>
  interpolate(frame, [at, at + len], [0, 1], { ...clamp, easing: ease });
const mod = (n: number, m: number) => ((n % m) + m) % m;

/** Layout (logical px). The machine stays in the Reels-safe band. */
const BODY = { left: 120, top: 480, width: 780, height: 860 };
const WINDOW = { top: 150, height: 300, pad: 12 };
const REELS = 3;
const CELL = (BODY.width - 60 - 2 * WINDOW.pad - (REELS - 1) * 12) / REELS;
const VIEW_H = WINDOW.height - 2 * WINDOW.pad;

/** Reel `r` position (in cells, float) at `frame`; integer = a brand centred. */
const reelPos = (r: number, frame: number) => {
  let p = r * 4; // at rest: three different brands
  for (const [j, at] of T.pulls.entries()) {
    const start = at + T.spin;
    if (frame < start) break;
    const end = p + mod(WINNERS[j] - p, M) + M * (2 + r);
    const len = T.run + r * T.stagger;
    p =
      frame >= start + len
        ? end
        : p + (end - p) * interpolate(frame, [start, start + len], [0, 1], {
            ...clamp,
            easing: Easing.out(Easing.back(1.1)),
          });
  }
  return p;
};

/** Which pull's payout is showing (-1 before the first). */
const shownPull = (frame: number) =>
  T.pulls.reduce((j, at, k) => (frame >= at + T.payout ? k : j), -1);

const Reel: React.FC<{ r: number }> = ({ r }) => {
  const frame = useCurrentFrame();
  const p = reelPos(r, frame);
  const speed = Math.abs(p - reelPos(r, frame - 1));
  const base = Math.floor(p);
  // A small bump when this reel lands.
  const landed = T.pulls
    .map((at) => at + T.spin + T.run + r * T.stagger)
    .filter((t) => frame >= t)
    .pop();
  const bump =
    landed === undefined
      ? 0
      : Math.sin(Math.PI * Math.min(1, (frame - landed) / 8)) * (frame - landed < 8 ? 1 : 0);

  return (
    <div
      style={{
        position: "relative",
        width: CELL,
        height: VIEW_H,
        borderRadius: 16,
        // Light blue, so white logo tiles still read as tiles.
        background: LOOK.mist,
        overflow: "hidden",
      }}
    >
      <div style={{ position: "absolute", inset: 0, filter: speed > 0.05 ? `blur(${Math.min(6, speed * 3)}px)` : undefined }}>
        {[-2, -1, 0, 1, 2].map((d) => {
          const k = base + d;
          const m: Merchant = MERCHANTS[mod(k, M)];
          const tile = CELL * 0.8;
          return (
            <div
              key={k}
              style={{
                position: "absolute",
                left: (CELL - tile) / 2,
                top: (VIEW_H - tile) / 2 + (p - k) * CELL,
                scale: Math.round(p) === k ? 1 + 0.08 * bump : 1,
              }}
            >
              <LogoTile m={m} size={tile} />
            </div>
          );
        })}
      </div>
      {/* Cylinder shading (top and bottom edges). */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.28), rgba(0,0,0,0) 26%, rgba(0,0,0,0) 74%, rgba(0,0,0,0.28))",
        }}
      />
    </div>
  );
};

const Bulbs: React.FC<{ frame: number; flash: boolean }> = ({ frame, flash }) => {
  const period = flash ? 3 : 8;
  const phase = Math.floor(frame / period) % 2;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "0 28px" }}>
      {Array.from({ length: 13 }, (_, k) => (
        <div
          key={k}
          style={{
            width: 14,
            height: 14,
            borderRadius: 7,
            background: (k + phase) % 2 ? LOOK.white : LOOK.blueSoft,
          }}
        />
      ))}
    </div>
  );
};

const Payout: React.FC = () => {
  const frame = useCurrentFrame();
  const j = shownPull(frame);
  // The shown payout leaves when the next pull starts.
  const next = T.pulls[j + 1];
  const leave = next === undefined ? 0 : ramp(frame, next, 8, Easing.in(Easing.cubic));
  if (j < 0) {
    const out = ramp(frame, T.pulls[0], 8, Easing.in(Easing.cubic));
    return (
      <div
        style={{
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: 48,
          color: LOOK.muted,
          opacity: 1 - out,
        }}
      >
        {SLOTS.prompt}
      </div>
    );
  }
  if (leave >= 1) return null;
  const m = MERCHANTS[WINNERS[j]];
  const at = T.pulls[j] + T.payout;
  const pop = (delay: number) =>
    spring({
      frame: frame - at - delay,
      fps: REEL.fps,
      config: { damping: 13, stiffness: 220, mass: 0.6 },
    });
  const line = (delay: number): React.CSSProperties => ({
    opacity: Math.min(1, pop(delay) * 2) * (1 - leave),
    translate: `0 ${(1 - pop(delay)) * 24 - leave * 20}px`,
  });
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div
        style={{
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: 54,
          letterSpacing: -1.5,
          color: LOOK.ink,
          ...line(0),
        }}
      >
        {m.name}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 16, ...line(4) }}>
        <span
          style={{
            fontFamily: LOOK.body,
            fontWeight: LOOK.semi,
            fontSize: 40,
            color: LOOK.inkSoft,
          }}
        >
          {FR.upTo}
        </span>
        <span
          style={{
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
            fontSize: 104,
            letterSpacing: -3,
            lineHeight: 1,
            color: GREEN,
          }}
        >
          {euros("fr", m.reward)}
        </span>
      </div>
      <div
        style={{
          marginTop: 6,
          fontFamily: LOOK.body,
          fontWeight: LOOK.medium,
          fontSize: 32,
          color: LOOK.inkSoft,
          ...line(8),
        }}
      >
        {FR.offer.per}
      </div>
    </div>
  );
};

const Lever: React.FC<{ frame: number }> = ({ frame }) => {
  const pull = T.pulls.reduce(
    (s, at) =>
      s +
      ramp(frame, at, 6, Easing.in(Easing.quad)) -
      ramp(frame, at + 8, 14, Easing.out(Easing.back(1.6))),
    0,
  );
  const pivot = BODY.top + 380;
  const ball = BODY.top + 90 + pull * 230;
  const x = BODY.left + BODY.width + 34;
  return (
    <>
      <div
        style={{
          position: "absolute",
          left: x - 7,
          top: ball,
          width: 14,
          height: pivot - ball,
          borderRadius: 7,
          background: LOOK.hairline,
        }}
      />
      <div
        style={{
          position: "absolute",
          left: BODY.left + BODY.width - 4,
          top: pivot - 30,
          width: 52,
          height: 70,
          borderRadius: "0 18px 18px 0",
          background: LOOK.white,
          boxShadow: FLOAT_SHADOW,
        }}
      />
      <div
        style={{
          position: "absolute",
          left: x - 34,
          top: ball - 34,
          width: 68,
          height: 68,
          borderRadius: 34,
          background: LOOK.red,
          boxShadow: FLOAT_SHADOW,
        }}
      />
    </>
  );
};

/** Payline markers: two small triangles at the window's edges. */
const Marker: React.FC<{ side: "left" | "right" }> = ({ side }) => (
  <svg
    width={22}
    height={30}
    viewBox="0 0 22 30"
    style={{
      position: "absolute",
      top: WINDOW.top + WINDOW.height / 2 - 15,
      [side]: 6,
      rotate: side === "right" ? "180deg" : undefined,
    }}
  >
    <path d="M0 0L22 15L0 30Z" fill={LOOK.red} />
  </svg>
);

export const SlotsReel: React.FC = () => {
  // Thrown here, not at import: the bundle holds every video, and only
  // this one needs three brands.
  if (M < T.pulls.length) {
    throw new Error(
      `Slots reel: needs ${T.pulls.length} live brands, the snapshot has ${M}. Run \`npm run sync:brands\`.`,
    );
  }
  const frame = useCurrentFrame();
  const j = shownPull(frame);
  const flash = j >= 0 && frame - (T.pulls[j] + T.payout) < 24;
  const out = interpolate(frame, [T.exit, T.exit + 10], [0, 1], {
    ...clamp,
    easing: Easing.in(Easing.quad),
  });

  return (
    <ReelFrame
      hook={SLOTS.hook}
      exitAt={T.exit}
      end={{
        at: T.end,
        line: SLOTS.end.line,
        sub: SLOTS.end.sub.replace("{n}", String(M)),
      }}
    >
      <div style={{ position: "absolute", inset: 0, opacity: 1 - out, scale: 1 - 0.05 * out }}>
        <Lever frame={frame} />
        <div
          style={{
            position: "absolute",
            ...BODY,
            borderRadius: 48,
            background: LOOK.white,
            boxShadow: FLOAT_SHADOW,
            overflow: "hidden",
          }}
        >
          {/* Marquee */}
          <div
            style={{
              height: 120,
              background: LOOK.blue,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              padding: "14px 0",
            }}
          >
            <Bulbs frame={frame} flash={flash} />
            <div style={{ display: "flex", justifyContent: "center" }}>
              <FrakLogo width={170} style={{ filter: "brightness(0) invert(1)" }} />
            </div>
            <Bulbs frame={frame + 4} flash={flash} />
          </div>

          {/* Window with the three reels */}
          <div
            style={{
              position: "absolute",
              top: WINDOW.top,
              left: 30,
              right: 30,
              height: WINDOW.height,
              padding: WINDOW.pad,
              borderRadius: 28,
              background: LOOK.ink,
              display: "flex",
              gap: 12,
              boxSizing: "border-box",
            }}
          >
            {Array.from({ length: REELS }, (_, r) => (
              <Reel key={r} r={r} />
            ))}
          </div>
          <Marker side="left" />
          <Marker side="right" />

          {/* Payout */}
          <div
            style={{
              position: "absolute",
              top: WINDOW.top + WINDOW.height + 34,
              left: 30,
              right: 30,
              height: 230,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Payout />
          </div>
          <div
            style={{
              position: "absolute",
              left: 40,
              right: 40,
              bottom: 34,
              fontFamily: LOOK.body,
              fontSize: 22,
              lineHeight: 1.3,
              color: LOOK.muted,
              textAlign: "center",
              opacity: j >= 0 ? 1 : 0,
            }}
          >
            {FR.footnote}
          </div>
        </div>
      </div>
    </ReelFrame>
  );
};
